import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  TextField,
  Switch,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import DeleteIcon from '@material-ui/icons/Delete';
import { format } from 'date-fns';

import { ApplicationState } from '../../../store';
import { fetchUsersRequest } from '../../../store/modules/admin/actions';
import { Container, TopBar, useStyles } from './UsersStyles';
import api from '../../../services/api';
import { toast } from 'react-toastify';

export default function UsersView() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { users, loading } = useSelector((state: ApplicationState) => state.admin);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });

  useEffect(() => {
    dispatch(fetchUsersRequest());
  }, [dispatch]);

  function getUserStatus(user: any) {
    if (user.isActive) return 'active';
    if (
      user.subscription &&
      user.subscription.status === 'trialing' &&
      user.subscription.currentPeriodEnd &&
      new Date(user.subscription.currentPeriodEnd) > new Date()
    ) {
      return 'trial';
    }
    return 'inactive';
  }

  function getStatusChip(user: any) {
    const status = getUserStatus(user);
    if (status === 'active') return <Chip label="Ativo" size="small" className={classes.chipActive} />;
    if (status === 'trial') return <Chip label="Trial" size="small" className={classes.chipTrial} />;
    return <Chip label="Inativo" size="small" className={classes.chipInactive} />;
  }

  async function handleToggleActive(userId: string) {
    try {
      await api.patch(`/admin/users/${userId}/toggleActive`);
      dispatch(fetchUsersRequest());
    } catch {
      toast.error('Erro ao alterar status.');
    }
  }

  async function handleCreateUser() {
    try {
      await api.post('/admin/users', form);
      toast.success('Usuario criado com sucesso!');
      setDialogOpen(false);
      setForm({ name: '', email: '', phone: '', password: '' });
      dispatch(fetchUsersRequest());
    } catch (err) {
      const msg = (err.response && err.response.data && err.response.data.error) || 'Erro ao criar usuario.';
      toast.error(msg);
    }
  }

  async function handleDeleteUser(userId: string, userName: string) {
    if (!window.confirm('Tem certeza que deseja excluir o usuario "' + userName + '"? Todos os dados (trades, pagamentos, depositos) serao removidos permanentemente.')) return;
    try {
      await api.delete('/admin/users/' + userId);
      toast.success('Usuario removido com sucesso!');
      dispatch(fetchUsersRequest());
    } catch {
      toast.error('Erro ao remover usuario.');
    }
  }

  const filtered = users.filter(u =>
    u.basicInfo.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading && users.length === 0) {
    return <CircularProgress style={{ margin: '40px auto', display: 'block' }} />;
  }

  return (
    <Container>
      <TopBar>
        <TextField
          className={classes.searchField}
          variant="outlined"
          size="small"
          placeholder="Buscar por nome ou email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Button variant="contained" color="primary" onClick={() => setDialogOpen(true)}>
          Criar Usuario
        </Button>
      </TopBar>

      <div className={classes.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Criado em</TableCell>
              <TableCell>Ativo</TableCell>
              <TableCell>Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(user => (
              <TableRow key={user._id}>
                <TableCell>{user.basicInfo.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{getStatusChip(user)}</TableCell>
                <TableCell>{format(new Date(user.createdAt), 'dd/MM/yyyy')}</TableCell>
                <TableCell>
                  <Switch
                    checked={user.isActive}
                    onChange={() => handleToggleActive(user._id)}
                    color="primary"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Link to={`/admin/users/${user._id}`}>
                      <IconButton size="small">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Link>
                    <IconButton size="small" onClick={() => handleDeleteUser(user._id, user.basicInfo.name)}>
                      <DeleteIcon fontSize="small" style={{ color: '#FF6347' }} />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Criar Usuario</DialogTitle>
        <DialogContent>
          <div className={classes.dialogContent}>
            <TextField
              label="Nome"
              variant="outlined"
              size="small"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              label="Email"
              variant="outlined"
              size="small"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
            />
            <TextField
              label="Telefone"
              variant="outlined"
              size="small"
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })}
            />
            <TextField
              label="Senha"
              variant="outlined"
              size="small"
              type="password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleCreateUser}>
            Criar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
